import { KeyboardEvent, useContext, useEffect, useMemo, useState } from "react";

import {
  AmplitudeFlagKey,
  getCountryCodeFromOrganizationCountry,
  hasPermissionOnBank,
  hasPermissionOnContacts,
} from "@simplyk/common";
import { useSnackbar } from "notistack";
import { useForm } from "react-hook-form";

import { RecaptchaAction } from "../../../common/constants/recaptcha";
import { CurrentUserContext } from "../../../common/contexts/CurrentUserContext";
import { toPhoneInputDigits } from "../../../common/helpers/phone";
import { trpc } from "../../../common/helpers/trpc";
import { useAmplitudeFeatureFlag } from "../../../common/hooks/amplitude/useAmplitudeFeatureFlag";
import { useProtectByStrongAuth } from "../../../common/hooks/useProtectByStrongAuth";
import { useSendSmsCooldown } from "../../../common/hooks/useSendSmsCooldown";

import { Mandatory2FAStepName } from "./types";

import { isReviewApp } from "@/common/constants/env";
import { useTranslate } from "@/common/hooks/useTranslate";

export const useMandatory2FASetup = () => {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const { mutateWithSmsCodeAndRecaptcha } = useProtectByStrongAuth("activate mandatory 2fa");

  const { currentUser, organization, refetchUser, connectedAsAdministrator } = useContext(CurrentUserContext);

  const [step, setStep] = useState<Mandatory2FAStepName>(Mandatory2FAStepName.Introduction);

  const { mutateAsync: activateSmsMFA, isPending: isLoadingSendSms } = trpc.activateSmsMFA.useMutation();

  const { sendSmsCooldown, triggerCooldown, isCooldownActive } = useSendSmsCooldown();

  const isSendButtonDisabled = isCooldownActive || isLoadingSendSms;

  const defaultPhoneNumber = currentUser?.phone || organization?.phoneNumber;
  const organizationCountry = organization?.country;
  const phoneNumberDigits = useMemo(
    () =>
      toPhoneInputDigits(
        defaultPhoneNumber,
        organizationCountry ? getCountryCodeFromOrganizationCountry(organizationCountry) : null
      ),
    [defaultPhoneNumber, organizationCountry]
  );

  const { isActive: allowedMfaMandatoryFeature } = useAmplitudeFeatureFlag({
    flagKey: AmplitudeFlagKey.MfaMandatoryForBankPermissionUsers,
    userProperties: {},
    skip: !organization || !currentUser,
  });

  const form = useForm<{ phoneNumber: string }>({
    defaultValues: { phoneNumber: phoneNumberDigits },
    shouldUnregister: true,
  });

  // Determine if the modal should be displayed
  const hasBankOrContactPermission =
    hasPermissionOnBank(currentUser?.newPermissions) || hasPermissionOnContacts(currentUser?.newPermissions);
  const hasBankMfaActivated = Boolean(currentUser?.isSmsMfaActivated || currentUser?.isTotpMfaActivated);
  const hasFourthPaymentDate = Boolean(organization?.fourthPaymentDate);
  const isAuthenticated = Boolean(currentUser?.id);

  const shouldDisplayModal =
    !isReviewApp &&
    allowedMfaMandatoryFeature &&
    isAuthenticated &&
    hasFourthPaymentDate &&
    hasBankOrContactPermission &&
    !hasBankMfaActivated &&
    !connectedAsAdministrator;

  const onSubmitForm = form.handleSubmit(async (data: { phoneNumber: string }) => {
    const { phoneNumber: phone } = data;
    const phoneNumber = phone?.startsWith("+") ? phone : `+${phone}` || "";
    const response = await mutateWithSmsCodeAndRecaptcha(RecaptchaAction.ActivateSmsMFA, "", activateSmsMFA, {
      phoneNumber,
    });
    triggerCooldown();
    if (response.error) {
      enqueueSnackbar(
        t(
          "dashboard",
          response.error.code === "send_sms_otp_invalid_phone_number"
            ? "settings.twoFactorAuthBySmsModal.setup.phoneNumber.invalid"
            : response.error.code === "send_sms_otp_duplicate_member_phone_number_for_member"
              ? "settings.twoFactorAuthBySmsModal.setup.phoneNumber.differentPhoneNumber"
              : "common.gqlError"
        ),
        { vibe: "danger" }
      );
    } else if (response.data) {
      enqueueSnackbar(t("dashboard", "settings.twoFactorAuthBySmsModal.setup.success"), { vibe: "positive" });
      void refetchUser();
    }
  });

  const submitOnKeyDown = async (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void onSubmitForm();
    }
  };

  useEffect(() => {
    form.setValue("phoneNumber", phoneNumberDigits);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phoneNumberDigits]);

  return {
    shouldDisplayModal,
    step,
    setStep,
    form,
    isLoadingSendSms,
    isSendButtonDisabled,
    sendSmsCooldown,
    onSubmitForm,
    submitOnKeyDown,
  };
};
